import type { components } from "./schema";
import { scopeHeaders, type ListScope } from "@/lib/api/distributors";
import { apiClient } from "./client";
import { organizationsChanged } from "./orgEvents";
import { fetchAllPages } from "./paginate";
import type { OwnershipFields } from "./ownership";

/** The organization lifecycle states the API reports. */
export type CompanyStatus = components["schemas"]["OrgStatusEnum"];

export interface Company extends OwnershipFields {
  id: string;
  name: string;
  /** Owning MSP id, represented as an organization UUID string. */
  msp: string;
  msp_name: string;
  is_active: boolean;
  status?: CompanyStatus;
  user_count?: number;
  credential_count?: number;
}

export interface CompanyCreateInput {
  name: string;
  /** Target MSP organization UUID. Omit to let backend derive from caller scope. */
  msp?: string | null;
}

export interface CompanyUpdateInput {
  name?: string;
  /** Assign the responsible user (owner). User UUID, not a number. */
  owner?: string | null;
  /**
   * Switch the company off (false) or back on (true). Moves with `status` on
   * the server, and contradicting it is refused.
   */
  is_active?: boolean;
  status?: Extract<CompanyStatus, "active" | "suspended">;
}

/**
 * What a transfer answers. The move itself, and what it did beyond moving the
 * row: memberships of groups the company is no longer under are removed on the
 * way, and the administrator should read that here rather than discover it
 * from a Members tab later.
 */
export type CompanyTransferResponse =
  components["schemas"]["CompanyTransferResponse"];
export type CompanyTransferRemovedMembership =
  components["schemas"]["CompanyTransferRemovedMembership"];

export const companiesApi = {
  /**
   * Every Company in scope, following the server's pages to the end.
   *
   * Takes no `page_size`. The old one was misleading twice over: the server
   * capped it at 100 whatever was asked, and the screens that passed nothing at
   * all rendered the first 25 as though that were the whole list.
   */
  list: async (opts?: { scope?: ListScope }): Promise<Company[]> => {
    return fetchAllPages<Company>("/companies/", scopeHeaders(opts?.scope));
  },
  retrieve: async (companyId: string): Promise<Company> => {
    const response = await apiClient.get(`/companies/${companyId}/`);
    return response.data;
  },
  create: async (data: CompanyCreateInput): Promise<Company> => {
    const response = await apiClient.post("/companies/", data);
    organizationsChanged();
    return response.data;
  },
  /**
   * Pass the `scope` the company was listed with, so the scope picker cannot
   * hide the row being changed.
   */
  update: async (
    companyId: string,
    data: CompanyUpdateInput,
    opts?: { scope?: ListScope },
  ): Promise<Company> => {
    const response = await apiClient.patch(`/companies/${companyId}/`, data, {
      headers: scopeHeaders(opts?.scope),
    });
    organizationsChanged();
    return response.data;
  },
  destroy: async (companyId: string): Promise<void> => {
    await apiClient.delete(`/companies/${companyId}/`);
    organizationsChanged();
  },
  transfer: async (
    companyId: string,
    targetMspId: string,
  ): Promise<CompanyTransferResponse> => {
    const response = await apiClient.post(`/companies/${companyId}/transfer/`, {
      target_msp_id: targetMspId,
    });
    organizationsChanged();
    return response.data;
  },
  approve: async (companyId: string): Promise<{ status: string }> => {
    const response = await apiClient.post(`/companies/${companyId}/approve/`);
    organizationsChanged();
    return response.data;
  },
  reject: async (companyId: string): Promise<{ status: string }> => {
    const response = await apiClient.post(`/companies/${companyId}/reject/`);
    organizationsChanged();
    return response.data;
  },
};
